import React from 'react';
import { CheckDefinition, Operator } from '../../../../interfaces/dynamicForms/FormBuilder';
import FormInput from './FormInput';
import { Button } from 'react-bootstrap';
import "./FormBuilder.css";

// Operators supported by the 'FIELD_CHECK' type in your RuleEngine
const FIELD_CHECK_OPERATORS: Operator[] = [
    'EQUALS',
    'CONTAINS',
    'CONTAINS_ANY',
    'CONTAINS_ALL',
    'DOES_NOT_CONTAIN',
    'DOES_NOT_CONTAIN_ANY',
    'DOES_NOT_CONTAIN_ALL'
];

interface CheckEditorProps {
    check: CheckDefinition;
    updateCheck: (updatedCheck: CheckDefinition) => void;
    removeCheck: () => void;
    allQuestionIds: string[];
}

const CheckEditor: React.FC<CheckEditorProps> = ({ check, updateCheck, removeCheck, allQuestionIds }) => {

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newType = e.target.value as 'FIELD_CHECK' | 'TIME_DIFF';
        // Reset the check object to a default state for the selected type
        if (newType === 'TIME_DIFF') {
            updateCheck({
                type: 'TIME_DIFF',
                startField: '',
                endField: '',
                minMinutes: 0
            });
        } else {
            updateCheck({
                type: 'FIELD_CHECK',
                field: '',
                operator: 'EQUALS',
                value: ''
            });
        }
    };

    const handleChange = (field: keyof CheckDefinition, value: any) => {
        // For the number input, ensure the value is a number
        const finalValue = field === 'minMinutes' ? Number(value) : value;
        updateCheck({ ...check, [field]: finalValue });
    };

    return (
        <>
            <div className="top top_bar_flx">
                <div className="custom_input_flled mb-0">
                    <select
                        style={{maxWidth: 'unset'}}
                        value={check.type || 'FIELD_CHECK'}
                        onChange={handleTypeChange}
                        className="sm_input filled_white"
                    >
                        <option value="FIELD_CHECK">Field Check</option>
                        <option value="TIME_DIFF">Time Difference Check</option>
                    </select>
                </div>
                <div className="top_right_flx">
                    <Button variant='danger' className='btn_sm' onClick={removeCheck}>Remove</Button>
                </div>
            </div>

            {/* Render UI based on the check type */}
            {check.type === 'TIME_DIFF' ? (
                <div className="row">
                    {/* Start Field */}
                    <div className='col-md-4 labelBlock'>
                        <div className='custom_input_flled'>
                            <label>Start Field (Time)</label>
                            <select
                                style={{maxWidth: 'unset'}}
                                value={check.startField}
                                onChange={(e) => handleChange('startField', e.target.value)}
                                className="sm_input filled_white"
                            >
                                <option value="">Select field...</option>
                                {allQuestionIds.map(id => <option key={id} value={id}>{id}</option>)}
                            </select>
                        </div>
                    </div>
                    {/* End Field */}
                    <div className='col-md-4 labelBlock'>
                        <div className='custom_input_flled'>
                            <label>End Field (Time)</label>
                            <select
                                style={{maxWidth: 'unset'}}
                                value={check.endField}
                                onChange={(e) => handleChange('endField', e.target.value)}
                                className="sm_input filled_white"
                            >
                                <option value="">Select field...</option>
                                {allQuestionIds.map(id => <option key={id} value={id}>{id}</option>)}
                            </select>
                        </div>
                    </div>
                    {/* Min Minutes */}
                    <div className='col-md-4 labelBlock'>
                        <div className='custom_input_flled'>
                            <FormInput
                                label="Min Minutes"
                                id={`check-${check.startField}-minMinutes`}
                                type="number"
                                value={check.minMinutes ?? 0}
                                onChange={(e) => handleChange('minMinutes', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="row">
                    {/* Field */}
                    <div className='col-md-4 labelBlock mt-3'>
                        <div className='custom_input_flled'>
                            <label>Field</label>
                            <select
                                style={{maxWidth: 'unset'}}
                                value={check.field}
                                onChange={(e) => handleChange('field', e.target.value)}
                                className="sm_input filled_white"
                            >
                                <option value="">Select field...</option>
                                {allQuestionIds.map(id => <option key={id} value={id}>{id}</option>)}
                            </select>
                        </div>
                    </div>
                    {/* Operator */}
                    <div className='col-md-4 labelBlock'>
                        <div className='custom_input_flled'>
                            <label>Operator</label>
                            <select
                                style={{maxWidth: 'unset'}}
                                value={check.operator}
                                onChange={(e) => handleChange('operator', e.target.value)}
                                className="sm_input filled_white"
                            >
                                {FIELD_CHECK_OPERATORS.map(op => <option key={op} value={op}>{op}</option>)}
                            </select>
                        </div>
                    </div>
                    {/* Value */}
                    <div className="col-md-4 labelBlock">
                        <div className="custom_input_flled">
                            <FormInput
                                label="Value"
                                id={`check-${check.field}-value`}
                                value={check.value}
                                onChange={(e) => handleChange('value', e.target.value)}
                            />
                            <div className="stats_filled mt-2">
                                <p>For operators like CONTAINS_ANY, use a comma-separated list (e.g., val1,val2).</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CheckEditor;